/* eslint-disable no-param-reassign */
import axios from 'axios';
import { keycloak } from '../keycloak';
import { refreshAccessToken } from './API/auth';

const baseConfig = {
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {},
};

const request = axios.create(baseConfig);

// Flag per tracciare se stiamo già facendo un refresh
let isRefreshing = false;
let refreshSubscribers = [];
let refreshRejectCallbacks = [];
let isLoggingOut = false;
// Reference to UserContext.logout — set by initAuth(); used by forceReauth() (FR-2.1)
let logoutCallback = null;

// Named error class for token polling timeout (FR-3)
class AuthTokenUnavailableError extends Error {
  constructor() {
    super('Keycloak token unavailable after polling timeout');
    this.name = 'AuthTokenUnavailableError';
  }
}

const clearStoredTokens = () => {
  localStorage.removeItem('kc_token');
  localStorage.removeItem('kc_refreshToken');
  localStorage.removeItem('kc_idToken');
};

const forceReauth = (logoutFn) => {
  if (isLoggingOut) {
    return;
  }
  isLoggingOut = true;
  if (typeof logoutFn === 'function') logoutFn();
  clearStoredTokens();
  if (keycloak?.authenticated) {
    keycloak.logout({ redirectUri: window.location.origin });
  } else {
    keycloak?.login?.({ redirectUri: window.location.origin });
  }
};

// Sottoscrivi i refresh token per retry le richieste in coda
const subscribeTokenRefresh = (cb, rejectCb) => {
  refreshSubscribers.push(cb);
  if (rejectCb) refreshRejectCallbacks.push(rejectCb);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  refreshRejectCallbacks = [];
};

const onRefreshFailed = (error) => {
  refreshRejectCallbacks.forEach((cb) => cb(error));
  refreshSubscribers = [];
  refreshRejectCallbacks = [];
};

// Helper per aspettare che il token sia disponibile (FR-3.1)
const waitForToken = () =>
  new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 50; // 50 * 100ms = 5 secondi
    const checkToken = () => {
      if (keycloak?.token) {
        resolve();
      } else if (attempts >= maxAttempts) {
        reject(new AuthTokenUnavailableError());
      } else {
        attempts += 1;
        setTimeout(checkToken, 100);
      }
    };
    checkToken();
  });

/**
 * Intercetta le requests per aggiungere il token e fare il refresh se necessario
 */
request.interceptors.request.use(async (req) => {
  // Aspetta che Keycloak sia inizializzato
  try {
    await waitForToken();
  } catch (err) {
    // Token unavailable after 5 s timeout — reject request without Authorization header.
    // Behaviour: AuthTokenUnavailableError propagates to the caller (FR-3.2a).
    return Promise.reject(err);
  }

  // Controlla se il token è scaduto o sta per scadere (entro 60 secondi)
  if (keycloak?.token) {
    const isTokenExpired = keycloak.isTokenExpired(60);

    if (isTokenExpired && keycloak.refreshToken) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Fai il refresh del token tramite il nostro endpoint
          const tokenResponse = await refreshAccessToken(keycloak.refreshToken);

          // Aggiorna i token di Keycloak
          keycloak.token = tokenResponse.access_token;
          keycloak.refreshToken = tokenResponse.refresh_token;

          // Salva i token nel localStorage
          localStorage.setItem('kc_token', tokenResponse.access_token);
          localStorage.setItem('kc_refreshToken', tokenResponse.refresh_token);

          // keycloak.tokenParsed is not updated after a backend-driven refresh because no
          // downstream code reads it post-refresh. keycloak-js owns tokenParsed at init time.

          isRefreshing = false;
          onRefreshed(tokenResponse.access_token);
        } catch (error) {
          isRefreshing = false;
          onRefreshFailed(error);
          console.warn(
            'Token refresh failed, will continue with existing token:',
            error
          );
          // DELIBERATE: forceReauth() is not called here.
          // If /api/v1/auth/refresh-token/ fails, the original request proceeds with the
          // known-expired token. The server 401 then triggers the response interceptor,
          // which calls forceReauth(). This avoids a double-logout race and keeps the
          // refresh path outside the request interceptor recursion boundary.
        }
      } else {
        // Se è già in corso un refresh, aspetta che finisca
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            () => {
              req.headers.Authorization = `Bearer ${keycloak?.token}`;
              resolve(req);
            },
            (error) => reject(error)
          );
        });
      }
    }
  }

  req.headers.Authorization = `Bearer ${keycloak?.token}`;
  return req;
});

request.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;
    if (status === 401 && !originalRequest?.handled401) {
      if (originalRequest) {
        originalRequest.handled401 = true;
      }
      forceReauth(logoutCallback);
    }
    return Promise.reject(error);
  }
);

export const initAuth = (auth, login, logout) => {
  logoutCallback = logout;
  isLoggingOut = false; // reset so subsequent 401s after re-auth are handled (FR-1.1)
  if (auth?.authenticated) {
    /* eslint-disable camelcase */
    const {
      given_name,
      family_name,
      email,
      preferred_username,
      resource_access,
      sub,
      session_state,
      jti,
    } = auth.tokenParsed;
    const sessionId = `${sub}:${session_state || jti || ''}`;
    login(
      {
        first_name: given_name,
        last_name: family_name,
        email,
        username: preferred_username,
        sessionId,
      },
      resource_access
    );
    /* eslint-enable camelcase */
  } else {
    logout();
  }
};

export default request;
